<template>
  <v-form @submit.prevent="submitUrl">
    <v-text-field
      v-model="urlInput"
      label="Enter URL"
      placeholder="Enter URL"
      :rules="urlValidationRules"
      :disabled="loaderStore.isLoading"
    />
    <v-btn
      size="x-large"
      variant="flat"
      block
      type="submit"
      color="primary"
      :disabled="!baseUrl || loaderStore.isLoading"
    >
      GO
    </v-btn>
  </v-form>
</template>

<script setup lang="ts">
  // Import functions from Vue
  import { ref } from 'vue';
  // Import store
  import { useUrlHistoryStore } from '@/store/urlHistoryStore'; // Adjust the import path as necessary
  import {useLoaderStore} from '@/store/loaderStore'; // Adjust the import path as necessary

  // Import the store and create a reference to it
  const urlHistoryStore = useUrlHistoryStore();
  const loaderStore = useLoaderStore();
  // emits the url-submitted event with the URL as a parameter that can be used by the parent component
  const emit = defineEmits<{ (e: 'url-submitted', url: string): void }>();

  // Create refs for the input and response fields
  const urlInput = ref('');
  const submitUrlResponse = ref('');
  const baseUrl = ref('');

  // Function to parse the URL, ensuring it starts with http or https
  const parseUrl = (url: string) => {
    if (!url) {
      throw new Error('Invalid URL');
    }
    return url.startsWith('http') ? new URL(url) : new URL('https://' + url);
  };

  // Vuetify validation rules that both validate and update baseUrl
  const urlValidationRules = [
    (value: string) => {
      if (!value) {
        baseUrl.value = '';
        return true;
      }
      try {
        const parsedUrl = parseUrl(value);

        if (parsedUrl.hostname.length < 2) {
          baseUrl.value = '';
          return 'Domain name is too short';
        }
        if (!parsedUrl.hostname.includes('.')) {
          baseUrl.value = '';
          return 'Domain name must contain at least one dot (.)';
        }
        if (value.endsWith('.')) {
          baseUrl.value = '';
          return 'Cannot end with .';
        }
        // If all checks pass, set the baseUrl state
        baseUrl.value = `${parsedUrl.protocol}//${parsedUrl.hostname}`;
        return true;
      } catch (error) {
        baseUrl.value = '';
        return 'Invalid URL format';
      }
    },
  ];

  // Function to handle form submission
  const submitUrl = async () => {
    if (!baseUrl.value) return;
    const urlExists = urlHistoryStore.existsUrl(baseUrl.value);
    if (urlExists) {
      urlHistoryStore.updateUrl(baseUrl.value);
      submitUrlResponse.value = `Updated URL: ${baseUrl.value}`;
    } else {
      urlHistoryStore.addUrl(baseUrl.value);
      submitUrlResponse.value = `Submitted URL: ${baseUrl.value}`;
    }

    // inform parent so it can close the dialog
    emit('url-submitted', baseUrl.value);

    // Reset fields after submission
    urlInput.value = '';
    baseUrl.value = '';

  };
</script>
